import { Box, LinearProgress, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import AcxButton from "components/UI/AcxButton";
import { ClassifierTestProgress } from "services/TestLLMClassifierService";

type Props = {
    progress: ClassifierTestProgress;
    isRunning: boolean;
    hasResults: boolean;
    onCancel: () => void;
};

export function TestProgressPanel({ progress, isRunning, hasResults, onCancel }: Props) {
    const [now, setNow] = useState(Date.now);
    const ticking = !!progress.startedAt && !progress.finishedAt && ["Queued", "Running"].includes(progress.state);
    useEffect(() => {
        setNow(Date.now());
        if (!ticking) return;
        const timer = window.setInterval(() => setNow(Date.now()), 1000);
        return () => window.clearInterval(timer);
    }, [ticking, progress.startedAt]);
    const startedAt = progress.startedAt ? new Date(progress.startedAt) : undefined;
    const elapsed = startedAt
        ? formatElapsed((progress.finishedAt ? Date.parse(progress.finishedAt) : now) - startedAt.getTime())
        : undefined;
    const total = progress.totalCount;
    const processed = progress.processedCount;
    const hasTotal = total !== undefined && total !== null;
    const percent = hasTotal && total > 0 ? Math.min(100, Math.round(processed / total * 100)) : 0;
    const stopping = isRunning && progress.cancellationRequested;
    const succeeded = progress.state === "Succeeded";
    const partial = !isRunning && !succeeded && hasResults;
    const title = stopping ? "Stopping test…"
        : progress.state === "Queued" ? "Waiting to start…"
        : progress.state === "Canceled" ? "Test stopped"
        : progress.state === "TimedOut" ? "Test time limit reached"
        : progress.state === "Failed" ? "Test interrupted"
        : succeeded ? "Test complete"
        : !hasTotal ? "Selecting conversations…"
        : processed >= total ? "Finalizing results…"
        : "Testing your model";
    const missingResults = (progress.receivedCount ?? processed) < processed;
    const description = progress.error ?? (missingResults
        ? `${progress.receivedCount} of ${processed} results received. Some results were missed during delivery. Run the test again for complete results.`
        : partial
        ? "Partial results are shown below. This test did not finish."
        : isRunning
            ? "Results appear below as conversations finish. Summary metrics reflect received results only."
            : hasResults ? "All available results are shown below." : "No conversations were processed.");

    return (
        <Box sx={{ p: 2, mb: 2, border: 1, borderColor: "divider", borderRadius: 1 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                <Box role="status" aria-live="polite">
                    <Typography fontWeight={600}>{title}</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {hasTotal ? `${processed} of ${total} conversations processed` : `${processed} conversations processed`}
                        {hasTotal && total > 0 ? ` · ${percent}%` : ""}
                    </Typography>
                </Box>
                {isRunning && (
                    <AcxButton variant="outlined" onClick={onCancel} disabled={!!stopping}>
                        {stopping ? "Stopping…" : "Cancel test"}
                    </AcxButton>
                )}
            </Stack>
            {startedAt && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Started at <time dateTime={progress.startedAt!} title={startedAt.toLocaleString()}>{startedAt.toLocaleTimeString()}</time>
                    {` · Elapsed ${elapsed}`}
                </Typography>
            )}
            <LinearProgress
                aria-label="Classifier test progress"
                variant={isRunning && (!hasTotal || total === 0) ? "indeterminate" : "determinate"}
                value={succeeded ? 100 : percent}
                color={progress.state === "Failed" || progress.state === "TimedOut" ? "error" : "primary"}
                sx={{ mt: 1.5, mb: 1, height: 6, borderRadius: 1 }}
            />
            <Typography variant="body2" color="text.secondary">{description}</Typography>
            {partial && progress.error && (
                <Typography variant="body2" color="text.secondary">Partial results are shown below.</Typography>
            )}
        </Box>
    );
}

function formatElapsed(milliseconds: number): string {
    const seconds = Math.max(0, Math.floor(milliseconds / 1000));
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    return `${hours ? `${hours}h ` : ""}${minutes % 60}m ${String(seconds % 60).padStart(2, "0")}s`;
}
